import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, CalendarDays, FileText, Receipt, Users, LogOut, ShieldCheck, Menu, X, History, Vault, Building2, UsersRound, CalendarRange, UserCircle, BadgeCheck, CalendarCheck2, Sun, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useState } from "react";

const empLinks = [
  { to: "/employe/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard },
  { to: "/employe/aujourdhui", label: "Aujourd'hui", icon: Sun },
  { to: "/employe/conges", label: "Mes congés", icon: CalendarDays },
  { to: "/employe/attestations", label: "Attestations", icon: FileText },
  { to: "/employe/bulletins", label: "Bulletins de paie", icon: Receipt },
  { to: "/employe/coffre-fort", label: "Coffre-fort", icon: Vault },
  { to: "/employe/equipe", label: "Équipe", icon: UsersRound },
  { to: "/employe/calendrier", label: "Calendrier équipe", icon: CalendarRange },
  { to: "/employe/documents-pro", label: "Documents pro", icon: BadgeCheck },
  { to: "/employe/profil", label: "Mon profil", icon: UserCircle },
];
const rhLinks = [
  { to: "/rh", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/rh/conges", label: "Demandes de congé", icon: CalendarDays },
  { to: "/rh/attestations", label: "Attestations", icon: FileText },
  { to: "/rh/bulletins", label: "Bulletins de paie", icon: Receipt },
  { to: "/rh/employes", label: "Employés", icon: Users },
  { to: "/rh/equipe", label: "Équipe", icon: UsersRound },
  { to: "/rh/calendrier", label: "Calendrier équipe", icon: CalendarRange },
  { to: "/rh/journal", label: "Journal", icon: History },
  { to: "/rh/connexions", label: "Journal de connexion", icon: KeyRound },
  { to: "/rh/parametres", label: "Paramètres entreprise", icon: Building2 },
  { to: "/rh/parametres-conges", label: "Paramètres congés", icon: CalendarCheck2 },
  { to: "/rh/profil", label: "Mon profil", icon: UserCircle },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const links = user?.role === "rh" ? rhLinks : empLinks;

  const initials = (user?.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const Sidebar = (
    <aside className="w-full h-full bg-white border-r border-slate-200 flex flex-col lg:w-64">
      <div className="h-16 flex items-center px-6 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-[#0066FF] flex items-center justify-center text-white">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <span className="font-heading font-black text-lg tracking-tight text-slate-900">FIO<span className="text-[#0066FF]">TELECOM</span></span>
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0066FF]/10 text-[#0066FF]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {l.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 px-2 py-2 mb-2">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-[#0066FF]/10 text-[#0066FF] font-semibold text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.role === "rh" ? "Ressources Humaines" : "Employé"}</div>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          data-testid="logout-button"
          className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Déconnexion
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* desktop */}
      <div className="hidden lg:block sticky top-0 h-screen">{Sidebar}</div>

      {/* mobile */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute inset-y-0 left-0 h-full w-72 max-w-[85vw] shadow-xl overflow-y-auto">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <header className="lg:hidden h-14 bg-white border-b border-slate-200 flex items-center px-3 sticky top-0 z-30">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            className="h-11 w-11 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 active:bg-slate-200"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <span className="ml-1 font-heading font-black text-lg tracking-tight">FIO<span className="text-[#0066FF]">TELECOM</span></span>
          <div className="ml-auto flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-[#0066FF]/10 text-[#0066FF] font-semibold text-xs">{initials}</AvatarFallback>
            </Avatar>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 fade-in" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
